import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Users, Star, Briefcase, MapPin, Calendar, Quote, Award, Network, X, Send } from 'lucide-react'
import { useAlumni } from '../contexts/AlumniContext'
import ResponsiveCarousel from './ResponsiveCarousel'

const Alumni = () => {
  const { t } = useTranslation()
  const {
    getActiveSuccessStories,
    getActiveTestimonials,
    getActiveStatistics,
    registerAlumni,
    submitSuccessStory,
    loading
  } = useAlumni()

  const [activeTab, setActiveTab] = useState('stories')
  const [showRegistrationForm, setShowRegistrationForm] = useState(false)
  const [showStoryForm, setShowStoryForm] = useState(false)
  const [registrationData, setRegistrationData] = useState({
    name: '',
    batchYear: '',
    phone: '',
    email: '',
    currentProfession: '',
    company: '',
    address: ''
  })
  const [storyData, setStoryData] = useState({
    name: '',
    batchYear: '',
    currentPosition: '',
    company: '',
    successStory: '',
    achievements: ''
  })

  // Get dynamic data from context
  const successStories = getActiveSuccessStories()
  const testimonials = getActiveTestimonials()
  const statistics = getActiveStatistics()

  // Icon mapping for statistics
  const getIconComponent = (iconName) => {
    const iconProps = { className: "w-8 h-8" }
    switch (iconName) {
      case 'Users':
        return <Users {...iconProps} />
      case 'Briefcase':
        return <Briefcase {...iconProps} />
      case 'Award':
        return <Award {...iconProps} />
      case 'Network':
        return <Network {...iconProps} />
      case 'Globe':
        return <Network {...iconProps} />
      case 'Star':
        return <Star {...iconProps} />
      default:
        return <Users {...iconProps} />
    }
  }

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault()
    
    try {
      // Prepare registration data
      const registration = {
        ...registrationData,
        graduationYear: registrationData.batchYear
      }
      
      // Call API
      const response = await registerAlumni(registration)
      
      // Show success message
      alert(response.message || t('alumni.registrationForm.successMessage'))
      
      // Reset form and close modal
      setShowRegistrationForm(false)
      setRegistrationData({
        name: '',
        batchYear: '',
        phone: '',
        email: '',
        currentProfession: '',
        company: '',
        address: ''
      })
    } catch (error) {
      console.error('Registration error:', error)
      alert(error.response?.data?.message || t('alumni.registrationForm.errorMessage'))
    }
  }

  const handleStorySubmit = async (e) => {
    e.preventDefault()
    
    try {
      // Prepare story data
      const submission = {
        name: storyData.name,
        batchYear: storyData.batchYear,
        title: storyData.currentPosition,
        company: storyData.company,
        story: storyData.successStory,
        achievements: storyData.achievements ? storyData.achievements.split('\n').filter(a => a.trim()) : []
      }
      
      // Call API
      const response = await submitSuccessStory(submission)
      
      // Show success message
      alert(response.message || t('alumni.storyForm.successMessage'))
      
      // Reset form and close modal
      setShowStoryForm(false)
      setStoryData({
        name: '',
        batchYear: '',
        currentPosition: '',
        company: '',
        successStory: '',
        achievements: ''
      })
    } catch (error) {
      console.error('Story submission error:', error)
      alert(error.response?.data?.message || t('alumni.storyForm.errorMessage'))
    }
  }

  const handleRegistrationChange = (e) => {
    setRegistrationData({
      ...registrationData,
      [e.target.name]: e.target.value
    })
  }
  
  const handleStoryChange = (e) => {
    setStoryData({
      ...storyData,
      [e.target.name]: e.target.value
    })
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  }

  // Show loading state
  if (loading) {
    return (
      <section id="alumni" className="py-12 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center min-h-96">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-purple"></div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="alumni" className="py-12 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-primary-purple mb-3 font-poppins leading-tight">
            {t('alumni.title')}
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('alumni.description')}
          </p>
        </motion.div>

        {/* Statistics */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-10"
        >
          {statistics.map((stat, index) => (
            <motion.div
              key={stat._id || index}
              variants={itemVariants}
              className="text-center p-4 lg:p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105"
            >
              <div className="text-primary-purple mb-2 sm:mb-3 flex justify-center">
                {getIconComponent(stat.icon)}
              </div>
              <div className="text-lg sm:text-2xl lg:text-3xl font-bold text-primary-purple mb-1 sm:mb-2 leading-tight">
                {stat.number}
              </div>
              <div className="text-gray-600 font-medium text-xs sm:text-sm lg:text-base leading-tight">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Tab Navigation - Success Stories / Testimonials */}
        <div className="flex justify-center mb-6">
          <div className="bg-white rounded-xl p-1.5 shadow-md w-full max-w-md">
            <button
              onClick={() => setActiveTab('stories')}
              className={`w-1/2 px-4 sm:px-6 py-3 rounded-lg font-semibold transition-all duration-300 text-sm sm:text-base ${
                activeTab === 'stories'
                  ? 'bg-primary-purple text-white shadow-md'
                  : 'text-gray-600 hover:text-primary-purple hover:bg-gray-50'
              }`}
            >
              {t('alumni.successStories')}
            </button>
            <button
              onClick={() => setActiveTab('testimonials')}
              className={`w-1/2 px-4 sm:px-6 py-3 rounded-lg font-semibold transition-all duration-300 text-sm sm:text-base ${
                activeTab === 'testimonials'
                  ? 'bg-primary-purple text-white shadow-md'
                  : 'text-gray-600 hover:text-primary-purple hover:bg-gray-50'
              }`}
            >
              {t('alumni.testimonials')}
            </button>
          </div>
        </div>

        {/* Success Stories Tab */}
        {activeTab === 'stories' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-10"
          >
            <div className="alumni-carousel-wrapper">
              <ResponsiveCarousel
                autoplay={true}
                autoplayDelay={4000}
                pauseOnHover={true}
                infinite={true}
                showNavigation={true}
                showDots={true}
                className=""
                desktopSlides={3}
                tabletSlides={2}
                mobileSlides={1}
                spaceBetween={24}
              >
                {successStories.map((story, index) => (
                  <div
                    key={story._id || index}
                    className="h-full px-1"
                  >
                    <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden h-full flex flex-col">
                      <div className="p-6 flex-1 flex flex-col">
                        <div className="flex items-center mb-5">
                          <img
                            src={story.image?.url || '/logo.jpeg'}
                            alt={story.name}
                            className="w-14 h-14 rounded-full object-cover mr-4 flex-shrink-0"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/logo.jpeg';
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-bold text-primary-purple leading-tight truncate">
                              {story.name}
                            </h3>
                            <p className="text-gray-600 text-sm">{t('alumni.batchOf')} {story.batchYear}</p>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <div className="flex items-center text-primary-blue mb-2">
                            <Briefcase className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span className="font-semibold text-sm line-clamp-1">{story.title}</span>
                          </div>
                          <div className="flex items-center text-gray-600 mb-4">
                            <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span className="text-sm line-clamp-1">{story.company}</span>
                          </div>
                        </div>

                        <p className="text-gray-700 mb-5 leading-relaxed text-sm line-clamp-3 flex-1">
                          {story.story}
                        </p>

                        <div className="mt-auto">
                          <h4 className="font-semibold text-primary-purple mb-2 text-sm">{t('alumni.keyAchievements')}</h4>
                          <ul className="space-y-1.5">
                            {story.achievements.slice(0, 3).map((achievement, idx) => (
                              <li key={idx} className="flex items-start text-xs text-gray-600">
                                <Award className="w-3 h-3 mr-2 mt-0.5 text-primary-yellow flex-shrink-0" />
                                <span className="line-clamp-2">{achievement}</span>
                              </li>
                            ))}
                            {story.achievements.length > 3 && (
                              <li className="text-xs text-primary-purple font-medium pl-5">
                                {t('alumni.moreAchievements', { count: story.achievements.length - 3 })}
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </ResponsiveCarousel>
            </div>
          </motion.div>
        )}

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-10"
          >
            <div className="alumni-carousel-wrapper">
              <ResponsiveCarousel
                autoplay={true}
                autoplayDelay={5000}
                pauseOnHover={true}
                infinite={true}
                showNavigation={true}
                showDots={true}
                className=""
                desktopSlides={3}
                tabletSlides={2}
                mobileSlides={1}
                spaceBetween={24}
              >
                {testimonials.map((testimonial, index) => (
                  <div
                    key={testimonial._id || index}
                    className="h-full px-1"
                  >
                    <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 relative h-full flex flex-col">
                      <div className="p-6 flex-1 flex flex-col">
                        <Quote className="w-7 h-7 text-primary-yellow mb-3 flex-shrink-0" />
                        <p className="text-gray-700 mb-5 leading-relaxed italic text-sm line-clamp-4 flex-1">
                          "{testimonial.testimonial}"
                        </p>
                        
                        <div className="flex items-center justify-between gap-3 mt-auto pt-4 border-t border-gray-100">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-primary-purple text-sm truncate">
                              {testimonial.name}
                            </h4>
                            <p className="text-gray-600 text-xs">Batch of {testimonial.batchYear}</p>
                          </div>
                          <div className="flex gap-0.5 flex-shrink-0">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <Star key={i} className="w-3.5 h-3.5 text-primary-yellow fill-current" />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </ResponsiveCarousel>
            </div>
          </motion.div>
        )}

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center bg-primary-purple rounded-2xl p-6 sm:p-8 lg:p-12 text-white"
        >
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-3 sm:mb-4">{t('alumni.joinNetworkTitle')}</h3>
          <p className="text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 opacity-90 leading-relaxed">
            {t('alumni.joinNetworkDescription')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button 
              onClick={() => setShowRegistrationForm(true)}
              className="bg-white text-primary-purple px-6 sm:px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 hover:scale-105 transition-all duration-300 cursor-pointer min-h-[48px] text-sm sm:text-base"
            >
              {t('alumni.registerAsAlumni')}
            </button>
            <button 
              onClick={() => setShowStoryForm(true)}
              className="border-2 border-white text-white px-6 sm:px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-purple hover:scale-105 transition-all duration-300 cursor-pointer min-h-[48px] text-sm sm:text-base"
            >
              {t('alumni.shareYourStory')}
            </button>
          </div>        </motion.div>
      </div>

      {/* Registration Form Modal */}
      {showRegistrationForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-primary-purple">{t('alumni.registrationForm.title')}</h3>
              <button 
                onClick={() => setShowRegistrationForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleRegistrationSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('alumni.registrationForm.fullName')}</label>
                <input
                  type="text"
                  name="name"
                  value={registrationData.name}
                  onChange={handleRegistrationChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  placeholder={t('alumni.registrationForm.fullNamePlaceholder')}
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('alumni.registrationForm.batchYear')}</label>
                <input
                  type="text"
                  name="batchYear"
                  value={registrationData.batchYear}
                  onChange={handleRegistrationChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  placeholder={t('alumni.registrationForm.batchYearPlaceholder')}
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('alumni.registrationForm.phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  value={registrationData.phone}
                  onChange={handleRegistrationChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  placeholder={t('alumni.registrationForm.phonePlaceholder')}
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('alumni.registrationForm.email')}</label>
                <input
                  type="email"
                  name="email"
                  value={registrationData.email}
                  onChange={handleRegistrationChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  placeholder={t('alumni.registrationForm.emailPlaceholder')}
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('alumni.registrationForm.profession')}</label>
                <input
                  type="text"
                  name="currentProfession"
                  value={registrationData.currentProfession}
                  onChange={handleRegistrationChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  placeholder={t('alumni.registrationForm.professionPlaceholder')}
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('alumni.registrationForm.company')}</label>
                <input
                  type="text"
                  name="company"
                  value={registrationData.company}
                  onChange={handleRegistrationChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  placeholder={t('alumni.registrationForm.companyPlaceholder')}
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('alumni.registrationForm.address')}</label>
                <textarea
                  name="address"
                  value={registrationData.address}
                  onChange={handleRegistrationChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  placeholder={t('alumni.registrationForm.addressPlaceholder')}
                />
              </div>
              
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowRegistrationForm(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-primary-purple text-white py-3 rounded-lg font-medium hover:bg-primary-magenta transition-colors flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  {t('alumni.registrationForm.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Story Submission Form Modal */}
      {showStoryForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-primary-purple">{t('alumni.storyForm.title')}</h3>
              <button 
                onClick={() => setShowStoryForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleStorySubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={storyData.name}
                  onChange={handleStoryChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  placeholder="Enter your full name"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">Batch Year *</label>
                <input
                  type="text"
                  name="batchYear"
                  value={storyData.batchYear}
                  onChange={handleStoryChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  placeholder="e.g., Class of 2015"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">Current Position *</label>
                <input
                  type="text"
                  name="currentPosition"
                  value={storyData.currentPosition}
                  onChange={handleStoryChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  placeholder="e.g., Senior Software Engineer"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">Company/Organization *</label>
                <input
                  type="text"
                  name="company"
                  value={storyData.company}
                  onChange={handleStoryChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  placeholder="Enter your company name"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('alumni.storyForm.story')}</label>
                <textarea
                  name="successStory"
                  value={storyData.successStory}
                  onChange={handleStoryChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  placeholder={t('alumni.storyForm.storyPlaceholder')}
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('alumni.storyForm.achievements')}</label>
                <textarea
                  name="achievements"
                  value={storyData.achievements}
                  onChange={handleStoryChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  placeholder={t('alumni.storyForm.achievementsPlaceholder')}
                />
              </div>
              
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowStoryForm(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-primary-purple text-white py-3 rounded-lg font-medium hover:bg-primary-magenta transition-colors flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  {t('alumni.storyForm.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  )
}

export default Alumni